import {
	API_ENDPOINTS,
	customToast,
	route,
	Modal,
	StringHelpers,
	replaceKeyWithValue,
	useLangContext,
	Variants,
	useDisabledContext,
	formatDate
}
	from 'carrier-fe';
import axios from 'axios';
import { ClaimViewData } from '../types/claim';

function CancelClaimModal({
	modalOpen,
	modalClose,
	claimId,
	storeData,
	onSuccess,
}: {
	modalOpen: boolean;
	modalClose: () => void;
	claimId: string;
	storeData: ClaimViewData;
	onSuccess?: () => Promise<void>;
}) {
	// Hooks and Contexts
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();

	const handleCancel = async () => {
		setDisabled(true);

		try {
			let response = await axios.patch(
				route(API_ENDPOINTS.REGISTRATION.CLAIM.CANCEL, {
					claimId: String(claimId),
				})
			);

			customToast({
				title: response?.data?.message || '',
				variant: Variants.Success,
			});

			if (onSuccess) {
				await onSuccess();
			}

			modalClose();
		} catch (error: any) {
			customToast({
				title: error?.response?.data?.message || '',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const modelName = StringHelpers.lower(crud?.models?.claim || 'claim');

	const getTitle = () => {
		return StringHelpers.title(
			replaceKeyWithValue(
				crud?.modals?.cancel?.title || 'Cancel :name',
				'name',
				modelName
			)
		);
	};

	const getConfirmText = () => {
		return disabled
			? crud?.buttons?.cancel_claim?.submitting || 'Cancelling claim...'
			: crud?.buttons?.cancel_claim?.default || 'Cancel claim';
	};

	const getCloseText = () => {
		return crud?.buttons?.cancel?.default || 'Cancel';
	};

	const getDescription = () => {
		return replaceKeyWithValue(
			crud?.modals?.cancel?.description || 'Are you sure you want to cancel this :name?',
			'name',
			modelName
		);
	};

	return (
		<Modal
			open={modalOpen}
			onClose={modalClose}
			title={getTitle()}
			confirmText={getConfirmText()}
			closeText={getCloseText()}
			onConfirm={handleCancel}
			disabled={disabled}
			variant={Variants.Danger}
		>
			<div>
				<p>{getDescription()}</p>
				<p>
					<strong>
						{`${StringHelpers.title(crud?.models?.customer || 'Customer')}: `}
					</strong>
					{storeData?.customer_full_name || '-'}
				</p>
				<p>
					<strong>
						{`${StringHelpers.title(fields?.company_name || 'Company')}: `}
					</strong>
					{StringHelpers.title(storeData?.company?.name || '-')}
				</p>
				<p>
					<strong>
						{`${StringHelpers.title(fields?.created_at || 'Date of claim')}: `}
					</strong>
					{storeData?.created_at ? formatDate(storeData.created_at) : '-'}
				</p>
				<p>
					<strong>
						{`${StringHelpers.title(fields?.status || 'Status')}: `}
					</strong>
					{storeData?.status_display || '-'}
				</p>
				<p>
					<strong>
						{`${StringHelpers.title(fields?.quantity_of_parts || 'Number of parts')}: `}
					</strong>
					{storeData?.parts_count ?? '-'}
				</p>
			</div>
		</Modal>
	);
}

export default CancelClaimModal;
